// DAS-2148: "Your Performance" modal for PortfolioAnalysisView
// Shows portfolio return attribution: weighted returns, top/bottom contributors,
// and sector attribution — with full transparency about data sources.

import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Paper, Tooltip, Chip, Collapse, IconButton,
  Table as MuiTable, TableHead, TableBody, TableRow, TableCell,
  TableContainer, TableSortLabel, Alert,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { BarChart } from '@mui/x-charts/BarChart';
import ClickableTickerChip from './ClickableTickerChip';

// ─── Utilities ───────────────────────────────────────────────────

const pf = (v) => { const n = parseFloat(v); return (v == null || isNaN(n)) ? null : n; };
const fmtPct = (v, d = 1) => v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(d)}%`;
const fmtWeight = (v) => v == null ? '—' : `${(v * 100).toFixed(1)}%`;

const perfColor = (v) => {
  if (v == null) return undefined;
  if (v >= 5) return '#1b5e20';
  if (v > 0) return '#2e7d32';
  if (v === 0) return '#757575';
  if (v > -5) return '#c62828';
  return '#b71c1c';
};

const perfBgColor = (v) => {
  if (v == null) return 'transparent';
  if (v >= 10) return 'rgba(27,94,32,0.12)';
  if (v >= 5) return 'rgba(76,175,80,0.10)';
  if (v > 0) return 'rgba(76,175,80,0.05)';
  if (v === 0) return 'transparent';
  if (v > -5) return 'rgba(229,57,53,0.05)';
  if (v > -10) return 'rgba(229,57,53,0.10)';
  return 'rgba(183,28,28,0.12)';
};

// ─── Period config ───────────────────────────────────────────────

const PERIODS = [
  { key: '1D', field: 'one_day_perf', label: '1 Day' },
  { key: '1W', field: 'one_week_performance', label: '1 Week' },
  { key: '1M', field: 'one_month_performance', label: '1 Month' },
  { key: '6M', field: 'six_month_performance', label: '6 Months' },
  { key: 'YTD', field: 'YTD', label: 'Year to Date' },
];

// ─── Data source documentation ───────────────────────────────────

const DATA_SOURCES = {
  portfolioReturn: {
    label: 'Portfolio Return',
    source: 'Ticker_Performance_Data',
    what: 'Weighted average of per-ticker period returns. Each ticker\'s return is weighted by its position value (Value_eur) relative to the total portfolio value.',
    meaning: 'The overall portfolio return for the selected period, accounting for position sizes. Larger positions contribute more to the total.',
    limitation: 'Only tickers with return data for the period are included. Not all positions may have data. Uses Value_eur for weighting — positions without a value are excluded.',
  },
  contribution: {
    label: 'Contribution',
    source: 'Ticker_Performance_Data + Portfolio_View',
    what: 'Contribution = position weight × ticker return for the period. Shows how much each position added to or subtracted from the total portfolio return.',
    meaning: 'A stock with +10% return but 1% weight contributes +0.10% to the portfolio. A stock with -5% but 20% weight subtracts -1.00%. This tells you what actually moved the needle.',
    limitation: 'Sum of contributions may not exactly equal the portfolio return due to rounding and missing data.',
  },
  sectorAttribution: {
    label: 'Sector Attribution',
    source: 'YF_Data (sector) + Ticker_Performance_Data (returns)',
    what: 'Groups positions by sector from yfinance. Per sector: weight = sum of position weights, return = value-weighted average return, contribution = sector weight × sector return.',
    meaning: 'Which sectors drove your portfolio performance. A sector with high weight and positive return is a major positive contributor.',
    limitation: 'Sector classification comes from yfinance — not all positions have sector data (funds, deposits are "Unclassified").',
  },
  benchmark: {
    label: 'Benchmark',
    source: 'yfinance (historical prices)',
    what: 'Period returns for a benchmark index (default: SPY — S&P 500 ETF) computed from daily close prices.',
    meaning: 'How your portfolio performed relative to the broad market. Outperformance (alpha) = portfolio return - benchmark return.',
    limitation: 'SPY is a US equity benchmark. May not be appropriate for portfolios with significant non-US or non-equity holdings.',
  },
};

const DataSourceChip = ({ sourceKey }) => {
  const info = DATA_SOURCES[sourceKey];
  if (!info) return null;
  return (
    <Tooltip
      arrow
      placement="bottom-start"
      title={
        <Box sx={{ maxWidth: 360, p: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>{info.label}</Typography>
          <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
            <strong>Data source:</strong> {info.source}
          </Typography>
          <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
            <strong>How it's calculated:</strong> {info.what}
          </Typography>
          <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
            <strong>What it means:</strong> {info.meaning}
          </Typography>
          <Typography variant="caption" display="block" sx={{ color: '#ffab91' }}>
            <strong>Limitation:</strong> {info.limitation}
          </Typography>
        </Box>
      }
    >
      <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help', ml: 0.5, verticalAlign: 'middle' }} />
    </Tooltip>
  );
};

// ─── Coverage-aware MetricCard ───────────────────────────────────

const MetricCard = ({ title, value, subtitle, color, sourceKey, coverageText, coveredTickers, missingTickers }) => {
  const [showTickers, setShowTickers] = useState(false);
  const hasTickers = (coveredTickers?.length > 0 || missingTickers?.length > 0);

  return (
    <Paper variant="outlined" sx={{ p: 2, flex: '1 1 140px', minWidth: 140 }}>
      <Box display="flex" alignItems="center">
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.6rem' }}>
          {title}
        </Typography>
        {sourceKey && <DataSourceChip sourceKey={sourceKey} />}
      </Box>
      <Typography variant="h5" sx={{ fontWeight: 700, color: color || 'text.primary', mt: 0.5 }}>
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="body2" sx={{ fontWeight: 600, color: color || 'text.secondary', mt: 0.25 }}>
          {subtitle}
        </Typography>
      )}
      {coverageText && (
        <Typography
          variant="caption"
          sx={{
            display: 'block', mt: 0.5,
            color: hasTickers ? 'primary.main' : 'text.disabled',
            cursor: hasTickers ? 'pointer' : 'default',
            textDecoration: hasTickers ? 'underline dotted' : 'none',
            '&:hover': hasTickers ? { color: 'primary.dark' } : {},
          }}
          onClick={hasTickers ? () => setShowTickers(prev => !prev) : undefined}
        >
          {coverageText} {hasTickers && (showTickers ? '▲' : '▼')}
        </Typography>
      )}
      <Collapse in={showTickers}>
        <Box sx={{ mt: 1 }}>
          {coveredTickers?.length > 0 && (
            <Box sx={{ mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 600, fontSize: '0.6rem' }}>
                Included:
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={0.3} mt={0.3}>
                {coveredTickers.map(t => (
                  <Chip key={t} label={t} size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: 'rgba(46,125,50,0.08)' }} />
                ))}
              </Box>
            </Box>
          )}
          {missingTickers?.length > 0 && (
            <Box>
              <Typography variant="caption" sx={{ color: '#c62828', fontWeight: 600, fontSize: '0.6rem' }}>
                Not included:
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={0.3} mt={0.3}>
                {missingTickers.map(t => (
                  <Chip key={t} label={t} size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: 'rgba(198,40,40,0.08)' }} />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

// ─── Computation ─────────────────────────────────────────────────

function computePerformance(tpData, yfData, data) {
  if (!data?.length) return null;

  const totalValue = data.reduce((s, d) => s + (pf(d.Value_eur) || 0), 0);
  const useWeights = totalValue > 0;

  const positions = data
    .filter(d => d.Ticker)
    .map(d => {
      const ticker = d.Ticker.toUpperCase();
      const tp = tpData[ticker] || {};
      const yf = yfData[ticker] || {};
      const value = pf(d.Value_eur) || 0;
      const weight = useWeights ? (totalValue > 0 ? value / totalValue : 0) : (1 / data.length);

      const returns = {};
      for (const p of PERIODS) {
        returns[p.key] = pf(tp[p.field]);
      }

      return {
        ticker,
        company: d.Company || '',
        value,
        weight,
        sector: yf.sector || null,
        industry: yf.industry || null,
        returns,
        hasData: PERIODS.some(p => returns[p.key] != null),
      };
    })
    .filter(p => p.weight > 0);

  if (positions.length === 0) return null;

  const portfolioReturns = {};
  const coverage = {};
  for (const p of PERIODS) {
    const valid = positions.filter(pos => pos.returns[p.key] != null);
    const missing = positions.filter(pos => pos.returns[p.key] == null);
    const totalW = valid.reduce((s, pos) => s + pos.weight, 0);
    if (totalW > 0) {
      portfolioReturns[p.key] = valid.reduce((s, pos) => s + pos.weight * pos.returns[p.key], 0) / totalW;
    } else {
      portfolioReturns[p.key] = null;
    }
    coverage[p.key] = {
      count: valid.length,
      total: positions.length,
      pct: totalW * 100,
      covered: valid.map(pos => pos.ticker).sort(),
      missing: missing.map(pos => pos.ticker).sort(),
    };
  }

  const contributions = {};
  for (const p of PERIODS) {
    contributions[p.key] = positions.map(pos => ({
      ...pos,
      return: pos.returns[p.key],
      contribution: pos.returns[p.key] != null ? pos.weight * pos.returns[p.key] : null,
    })).filter(c => c.contribution != null)
      .sort((a, b) => b.contribution - a.contribution);
  }

  const sectorMap = {};
  for (const pos of positions) {
    const sector = pos.sector || 'Unclassified';
    if (!sectorMap[sector]) sectorMap[sector] = { sector, positions: [], totalWeight: 0 };
    sectorMap[sector].positions.push(pos);
    sectorMap[sector].totalWeight += pos.weight;
  }

  const sectors = {};
  for (const p of PERIODS) {
    sectors[p.key] = Object.values(sectorMap).map(sg => {
      const valid = sg.positions.filter(pos => pos.returns[p.key] != null);
      const sectorW = valid.reduce((s, pos) => s + pos.weight, 0);
      const sectorReturn = sectorW > 0
        ? valid.reduce((s, pos) => s + pos.weight * pos.returns[p.key], 0) / sectorW
        : null;
      return {
        sector: sg.sector,
        weight: sg.totalWeight,
        return: sectorReturn,
        contribution: sectorReturn != null ? sg.totalWeight * sectorReturn : null,
        tickerCount: sg.positions.length,
        dataCount: valid.length,
      };
    }).sort((a, b) => (b.contribution || 0) - (a.contribution || 0));
  }

  return { portfolioReturns, coverage, contributions, sectors, positions, totalValue, useWeights };
}

// ─── Main component ──────────────────────────────────────────────

const PAModal_Performance = ({ tpData, yfData, data, source = 'portfolio', benchmarkData = null }) => {
  const [selectedPeriod, setSelectedPeriod] = useState('1M');
  const [methodOpen, setMethodOpen] = useState(false);
  const [orderBy, setOrderBy] = useState('contribution');
  const [order, setOrder] = useState('desc');
  const [sectorOrderBy, setSectorOrderBy] = useState('contribution');
  const [sectorOrder, setSectorOrder] = useState('desc');

  const perf = useMemo(() => computePerformance(tpData, yfData, data), [tpData, yfData, data]);

  const handleSort = (field) => {
    if (orderBy === field) setOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setOrderBy(field); setOrder('desc'); }
  };

  const handleSectorSort = (field) => {
    if (sectorOrderBy === field) setSectorOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setSectorOrderBy(field); setSectorOrder('desc'); }
  };

  if (!perf) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" sx={{ minHeight: 200 }}>
        <Typography color="text.secondary">No performance data available — requires ticker performance data</Typography>
      </Box>
    );
  }

  const periodContrib = perf.contributions[selectedPeriod] || [];
  const sortedContrib = [...periodContrib].sort((a, b) => {
    const av = a[orderBy] ?? -Infinity;
    const bv = b[orderBy] ?? -Infinity;
    return order === 'asc' ? av - bv : bv - av;
  });

  const periodSectors = perf.sectors[selectedPeriod] || [];
  const sortedSectors = [...periodSectors].sort((a, b) => {
    const av = a[sectorOrderBy] ?? -Infinity;
    const bv = b[sectorOrderBy] ?? -Infinity;
    return sectorOrder === 'asc' ? av - bv : bv - av;
  });

  const chartSectors = periodSectors.filter(s => s.contribution != null).sort((a, b) => (b.contribution || 0) - (a.contribution || 0));

  const bm = benchmarkData;

  return (
    <Box>
      {/* Methodology */}
      <Paper
        variant="outlined"
        sx={{ p: 1.5, mb: 2, bgcolor: '#f5f5f5', cursor: 'pointer' }}
        onClick={() => setMethodOpen(prev => !prev)}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <InfoOutlinedIcon fontSize="small" color="action" />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              How to read this — click (i) icons for data source details
            </Typography>
          </Box>
          <IconButton size="small">
            {methodOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        <Collapse in={methodOpen}>
          <Box mt={1.5}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>Portfolio returns</strong> are value-weighted averages of per-ticker period returns from Ticker_Performance_Data.
              {!perf.useWeights && ' Since no position values are available, equal weights are used.'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>Contribution</strong> = position weight x ticker return. It shows how much each holding added or subtracted
              from the overall portfolio return. The sum of all contributions equals the portfolio return.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Sector attribution</strong> groups your positions by sector (from yfinance) and shows which sectors
              drove performance. Positions without a sector (funds, deposits) are grouped as "Unclassified".
            </Typography>
          </Box>
        </Collapse>
      </Paper>

      {/* Section A: Portfolio Return Summary */}
      <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
        Portfolio Return
      </Typography>
      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
        {PERIODS.map(p => {
          const ret = perf.portfolioReturns[p.key];
          const cov = perf.coverage[p.key];
          const bmRet = bm?.returns?.[p.key];
          const alpha = (ret != null && bmRet != null) ? ret - bmRet : null;
          return (
            <MetricCard
              key={p.key}
              title={p.label}
              value={ret != null ? fmtPct(ret) : '—'}
              subtitle={bmRet != null ? `${bm.ticker}: ${fmtPct(bmRet)}${alpha != null ? ` · α: ${fmtPct(alpha)}` : ''}` : null}
              color={perfColor(ret)}
              sourceKey="portfolioReturn"
              coverageText={cov ? `${cov.count} of ${cov.total} positions (${Math.round(cov.pct)}% of value)` : null}
              coveredTickers={cov?.covered}
              missingTickers={cov?.missing}
            />
          );
        })}
      </Box>

      {/* Period selector for contributors and sectors */}
      <Box display="flex" gap={1} mb={2} flexWrap="wrap">
        {PERIODS.map(p => (
          <Chip
            key={p.key}
            label={p.key}
            size="small"
            variant={selectedPeriod === p.key ? 'filled' : 'outlined'}
            color={selectedPeriod === p.key ? 'primary' : 'default'}
            onClick={() => setSelectedPeriod(p.key)}
          />
        ))}
      </Box>

      {/* Section B: Top/Bottom Contributors */}
      <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
        Contributors ({PERIODS.find(p => p.key === selectedPeriod)?.label})
        <DataSourceChip sourceKey="contribution" />
      </Typography>

      {sortedContrib.length > 0 ? (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '40vh', mb: 3 }}>
          <MuiTable size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Ticker</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  <TableSortLabel active={orderBy === 'weight'} direction={orderBy === 'weight' ? order : 'desc'} onClick={() => handleSort('weight')}>
                    Weight
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  <TableSortLabel active={orderBy === 'return'} direction={orderBy === 'return' ? order : 'desc'} onClick={() => handleSort('return')}>
                    Return
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  <TableSortLabel active={orderBy === 'contribution'} direction={orderBy === 'contribution' ? order : 'desc'} onClick={() => handleSort('contribution')}>
                    Contribution
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Sector</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedContrib.map((row, idx) => {
                const isTop = idx < 5 && order === 'desc' && orderBy === 'contribution';
                const isBottom = idx >= sortedContrib.length - 5 && order === 'desc' && orderBy === 'contribution' && sortedContrib.length > 10;
                return (
                  <TableRow
                    key={row.ticker}
                    hover
                    sx={{ bgcolor: isTop ? 'rgba(46,125,50,0.04)' : isBottom ? 'rgba(198,40,40,0.04)' : undefined }}
                  >
                    <TableCell>
                      <ClickableTickerChip ticker={row.ticker} source={source} />
                      {row.company && (
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25 }}>
                          {row.company}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{fmtWeight(row.weight)}</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ bgcolor: perfBgColor(row.return) }}>
                      <Typography variant="body2" sx={{ color: perfColor(row.return), fontWeight: 500 }}>
                        {fmtPct(row.return)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ bgcolor: perfBgColor(row.contribution != null ? row.contribution * 10 : null) }}>
                      <Typography variant="body2" sx={{ color: perfColor(row.contribution != null ? row.contribution * 10 : null), fontWeight: 600 }}>
                        {row.contribution != null ? fmtPct(row.contribution, 2) : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{row.sector || '—'}</Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </MuiTable>
        </TableContainer>
      ) : (
        <Alert severity="info" variant="outlined" sx={{ mb: 3 }}>
          <Typography variant="caption">No contribution data available for {selectedPeriod}.</Typography>
        </Alert>
      )}

      {/* Section C: Sector Attribution */}
      <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
        Sector Attribution ({PERIODS.find(p => p.key === selectedPeriod)?.label})
        <DataSourceChip sourceKey="sectorAttribution" />
      </Typography>

      {chartSectors.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1, mb: 2 }}>
          <BarChart
            xAxis={[{ scaleType: 'band', data: chartSectors.map(s => s.sector.length > 16 ? s.sector.slice(0, 14) + '...' : s.sector) }]}
            series={[
              {
                data: chartSectors.map(s => s.contribution != null ? Number(s.contribution.toFixed(3)) : 0),
                label: 'Contribution (%)',
                color: '#1976d2',
              },
            ]}
            height={220}
            margin={{ left: 50, right: 20, top: 30, bottom: 60 }}
            slotProps={{ legend: { hidden: true } }}
          />
        </Paper>
      )}

      {sortedSectors.length > 0 && (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '35vh', mb: 3 }}>
          <MuiTable size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Sector</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  <TableSortLabel active={sectorOrderBy === 'weight'} direction={sectorOrderBy === 'weight' ? sectorOrder : 'desc'} onClick={() => handleSectorSort('weight')}>
                    Weight
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  <TableSortLabel active={sectorOrderBy === 'return'} direction={sectorOrderBy === 'return' ? sectorOrder : 'desc'} onClick={() => handleSectorSort('return')}>
                    Return
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  <TableSortLabel active={sectorOrderBy === 'contribution'} direction={sectorOrderBy === 'contribution' ? sectorOrder : 'desc'} onClick={() => handleSectorSort('contribution')}>
                    Contribution
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Tickers</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedSectors.map((row) => (
                <TableRow key={row.sector} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: row.sector === 'Unclassified' ? 400 : 500, fontStyle: row.sector === 'Unclassified' ? 'italic' : 'normal' }}>
                      {row.sector}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{fmtWeight(row.weight)}</TableCell>
                  <TableCell align="right" sx={{ bgcolor: perfBgColor(row.return) }}>
                    <Typography variant="body2" sx={{ color: perfColor(row.return), fontWeight: 500 }}>
                      {row.return != null ? fmtPct(row.return) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ bgcolor: perfBgColor(row.contribution != null ? row.contribution * 10 : null) }}>
                    <Typography variant="body2" sx={{ color: perfColor(row.contribution != null ? row.contribution * 10 : null), fontWeight: 600 }}>
                      {row.contribution != null ? fmtPct(row.contribution, 2) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="caption" color="text.secondary">
                      {row.dataCount}/{row.tickerCount}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </MuiTable>
        </TableContainer>
      )}
    </Box>
  );
};

export default PAModal_Performance;
